/**
 * Procesverbali i dorëzim–pranimit si dokument i printueshëm.
 *
 * Nuk përdor `pdfPrint.printReport`: ai është bërë për raporte — kokë, filtra,
 * një tabelë e gjatë dhe totale. Procesverbali është akt: ka numër, palë,
 * deklaratë përgjegjësie dhe dy nënshkrime, dhe pikërisht ato e bëjnë të vlefshëm.
 *
 * Dritarja e printimit e mbulon edhe "Shkarko PDF": çdo shfletues sot printon në
 * PDF. Një gjenerues i dytë (jsPDF) do të duhej të mbante të njëjtin layout në
 * dy vende, dhe herët a vonë do të rrëshqiste.
 */

import { DEKLARATA } from '../config/assets';

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

const day = (v) => {
  const s = String(v || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const [y, m, d] = s.split('-');
  return `${d}.${m}.${y}`;
};

const eur = (v) => (v === null || v === undefined || v === ''
  ? '—'
  : `${Number(v).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

export function handoverHtml(h, { company = 'NEXUMA', conditionLabel = (x) => x } = {}) {
  const items = h.items || [];
  const total = items.reduce((s, it) => s + (Number(it.value) || 0), 0);
  const isReturn = h.kind === 'return';
  const title = isReturn ? 'PROCESVERBAL — KTHIM I ASETIT' : 'PROCESVERBAL — DORËZIM / PRANIM I ASETIT';

  const rows = items.map((it, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td><strong>${esc(it.assetName)}</strong><br><span class="muted">${esc(it.assetCode)}</span></td>
      <td>${esc(it.serialNumber || '—')}</td>
      <td>${esc(conditionLabel(it.condition))}</td>
      <td>${esc(it.accessories || '—')}</td>
      ${isReturn ? `<td>${it.damageCost ? `${eur(it.damageCost)}${it.damageNote ? `<br><span class="muted">${esc(it.damageNote)}</span>` : ''}` : '—'}</td>` : ''}
      <td style="text-align:right">${eur(it.value)}</td>
    </tr>`).join('');

  const sig = (src, name, role) => `
    <div class="sig">
      ${src ? `<img src="${src}" alt="" />` : '<div class="sig-blank"></div>'}
      <div class="sig-line"></div>
      <div class="sig-name">${esc(name || '—')}</div>
      <div class="muted">${esc(role || '')}</div>
    </div>`;

  return `<!DOCTYPE html>
<html lang="sq"><head><meta charset="UTF-8" />
<title>${esc(h.docNo)}</title>
<style>
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color:#0F172A; font-size:11px; margin:0; }
  .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #3E8FA3; padding-bottom:8px; }
  .brand { font-size:18px; font-weight:800; color:#3E8FA3; letter-spacing:.5px; }
  .meta { text-align:right; font-size:10px; color:#475569; }
  h1 { font-size:14px; text-align:center; margin:16px 0 14px; letter-spacing:.6px; }
  .grid { display:flex; gap:24px; margin-bottom:14px; }
  .grid > div { flex:1; }
  .label { font-size:8.5px; text-transform:uppercase; letter-spacing:.06em; color:#64748B; font-weight:700; }
  .value { font-weight:600; }
  table { width:100%; border-collapse:collapse; margin:6px 0 10px; }
  th { background:#F1F5F9; font-size:9px; text-transform:uppercase; letter-spacing:.04em; text-align:left; padding:6px; border:1px solid #E2E8F0; }
  td { padding:6px; border:1px solid #E2E8F0; vertical-align:top; }
  .muted { color:#64748B; font-size:9.5px; }
  .total td { font-weight:800; background:#F8FAFC; }
  .decl { border:1px solid #E2E8F0; background:#F8FAFC; padding:10px; border-radius:4px; line-height:1.55; margin:12px 0 20px; }
  .sigs { display:flex; gap:40px; margin-top:28px; }
  .sig { flex:1; text-align:center; }
  .sig img { height:52px; object-fit:contain; }
  .sig-blank { height:52px; }
  .sig-line { border-top:1px solid #0F172A; margin-top:2px; }
  .sig-name { font-weight:700; margin-top:3px; }
  .draft { position:fixed; top:44%; left:12%; font-size:80px; color:rgba(148,163,184,.22);
           transform:rotate(-24deg); font-weight:800; letter-spacing:8px; }
  @media print { .noprint { display:none } }
</style></head>
<body>
  ${h.status !== 'confirmed' ? '<div class="draft">DRAFT</div>' : ''}
  <div class="head">
    <div><div class="brand">${esc(company)}</div><div class="muted">HR Platform</div></div>
    <div class="meta"><div>Nr: <strong>${esc(h.docNo)}</strong></div><div>Data: ${day(h.happenedOn)}${h.happenedAt ? `, ${esc(h.happenedAt)}` : ''}</div></div>
  </div>

  <h1>${title}</h1>

  <div class="grid">
    <div>
      <div class="label">Punonjësi ${isReturn ? 'dorëzues' : 'pranues'}</div>
      <div class="value">${esc(h.workerName || '—')}</div>
      <div class="muted">${esc(h.workerPosition || '')}</div>
      <div class="muted">${esc(h.workerEmail || '')}</div>
    </div>
    <div>
      <div class="label">Departamenti</div>
      <div class="value">${esc(h.departmentName || '—')}</div>
    </div>
    <div>
      <div class="label">Lokacioni</div>
      <div class="value">${esc(h.location || '—')}</div>
    </div>
  </div>

  <div class="label">Asetet ${isReturn ? 'e kthyera' : 'e dorëzuara'}</div>
  <table>
    <thead><tr>
      <th style="width:24px">#</th><th>Aseti</th><th>Serial / ID</th>
      <th>Gjendja</th><th>Aksesorët</th>${isReturn ? '<th>Dëmi</th>' : ''}<th style="text-align:right">Vlera</th>
    </tr></thead>
    <tbody>
      ${rows || `<tr><td colspan="${isReturn ? 7 : 6}" class="muted">Pa asete.</td></tr>`}
      <tr class="total"><td colspan="${isReturn ? 6 : 5}" style="text-align:right">TOTALI (pa TVSH)</td><td style="text-align:right">${eur(total)}</td></tr>
    </tbody>
  </table>

  ${h.comment ? `<div><span class="label">Komente</span><div>${esc(h.comment)}</div></div>` : ''}

  <div class="decl">${esc(isReturn ? DEKLARATA.ret : DEKLARATA.handover)}</div>

  <div class="sigs">
    ${isReturn
      ? sig(h.receiverSignature, h.workerName, `Dorëzoi (${h.workerPosition || 'punonjësi'})`)
      : sig(h.giverSignature, h.giverName, 'Dorëzoi (përfaqësuesi i kompanisë)')}
    ${isReturn
      ? sig(h.giverSignature, h.giverName, 'Pranoi (përfaqësuesi i kompanisë)')
      : sig(h.receiverSignature, h.workerName, `Pranoi (${h.workerPosition || 'punonjësi'})`)}
  </div>
</body></html>`;
}

/** Hap dritaren e printimit. Kthen `false` kur bllokuesi i pop-up-eve e ndal. */
export function printHandover(h, opts) {
  const win = window.open('', '_blank', 'width=900,height=1000,scrollbars=yes');
  if (!win) return false;
  win.document.write(handoverHtml(h, opts));
  win.document.close();
  win.focus();
  // Pritet ngarkimi i fotove të nënshkrimeve — pa këtë, printimi niset me
  // kutitë bosh aty ku duhet të jenë nënshkrimet.
  win.onload = () => setTimeout(() => win.print(), 250);
  return true;
}
